import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage20() {
  const contentRef = useRef(null);
   const links = {
    prev: "/islamic-trade-page-19", 
    index: "/index",                 
    next: "/islamic-trade-page-21", 
  };
  const titleText = "Rulings on Hiring and Leasing (Ijarah)";
  const questions = [
    {
      text: "1. What is a key requirement for a valid Ijaarah contract in Islamic law?",
      options: [
        {
          value: "A",
          label: "A. The compensation must be paid in gold only.",
        },
        {
          value: "B",
          label: "B. The benefit must be clearly defined and agreed upon.",
        },
        {
          value: "C",
          label: "C. The asset must be given as a gift afterward.",
        },
        {
          value: "D",
          label: "D. The lessee must be a Muslim.",
        },
      ],
      correct: "B",
    },
    {
      text: "2. According to Islamic law, can waqf (endowment) properties be rented??",
      options: [
        {
          value: "A",
          label: "A. No, because they are for religious use only.",
        },
        { value: "B", label: "B. Yes, but only to family members." },
        {
          value: "C",
          label: "C. Yes, if the rent supports the waqf’s intended purpose.",
        },
        {
          value: "D",
          label: "D. Yes, but only if the property is residential.",
        },
      ],
      correct: "c",
    },
    {
      text: "3. What happens to an Ijaarah contract if the rented asset is destroyed during the lease period?",
      options: [
        { value: "A", label: "A. The contract continues until the term ends." },
        { value: "B", label: "B. The lessee must pay the full rent." },
        { value: "C", label: "C. The contract is terminated immediately." },
        { value: "D", label: "D. The lessee becomes the owner of the asset." },
      ],
      correct: "C",
    },
    {
      text: "4.  In the case of hiring a married woman for work, what is required under Islamic law?",
      options: [
        { value: "A", label: "A. The husband’s explicit permission." },
        { value: "B", label: "B. A male guardian must co-sign." },
        { value: "C", label: "C. She must be single." },
        { value: "D", label: "D. She must only work from home." },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={20}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 20</b>! Today, we turn our attention to Ijaarah, the
        Islamic concept of hiring and leasing. Whether it's renting a home,
        hiring a worker, or leasing equipment, Ijaarah provides a clear and
        ethical framework for such agreements in accordance with Shariah. We
        will explore its essential conditions, practical applications, and how
        it differs from ownership-based contracts, ensuring fairness and
        transparency for both parties involved. <br />
        Ijaarah, meaning "to give something on rent" or "to hire," is a
        contractual agreement in Islamic jurisprudence where one party provides
        a service, benefit, or property to another in exchange for agreed-upon
        compensation. This arrangement is widely recognized in Islam as it
        facilitates mutual benefit while ensuring fairness and compliance with
        Shariah principles. The concept of Ijaarah is supported by evidence from
        the Quran and Hadith. In the Quran, the story of Prophet Musa (peace be
        upon him) highlights the validity of hiring; when the daughter of
        Shu’aib advises her father, saying,
        <br /> "Indeed, the best one you can hire is the strong and the
        trustworthy"{" "}
        <i>
          <a href="https://quran.com/28/26" target="blank">
            (Surah Al-Qasas 28:26)
          </a>
        </i>
        <br /> Similarly, in the Sunnah, the Prophet Muhammad (peace be upon
        him) hired Abdullah bin Uraiqit, a non-Muslim guide, to safely escort
        him and Abu Bakr (may Allah be pleased with him) during their migration
        from Mecca to Medina, showcasing the permissibility of hiring services
        irrespective of the hiree's faith. Ijaarah thus embodies an essential
        aspect of Islamic economic system, promoting ethical business practices
        and mutual cooperation.
        <br />
        <br />
        <b> Conditions of Ijaarah:</b> <br />
        <br />
        i) Clarity of Benefit : A fundamental condition of Ijaarah is that the
        benefit to be derived from the contract must be explicitly known and
        agreed upon by both parties. This ensures transparency and eliminates
        ambiguity, which is prohibited in Islamic transactions. For example,
        when renting a property, the tenant must clearly understand that the
        rent paid will allow them to use the property for specific purposes,
        such as living or running a business. Similarly, when hiring an employee
        for domestic work, the exact tasks they will perform such as cooking,
        cleaning, or childcare must be defined beforehand. In the case of hiring
        a teacher, it should be clear what subject they will teach, the duration
        of each session, and the total hours they will dedicate. This condition
        safeguards the rights of both parties and fosters trust and fairness,
        aligning the contract with Shariah principles.
        <br /> <br />
        ii) Clarity of Compensation : The second condition of Ijaarah is that
        the compensation to be paid in exchange for the lease or service must be
        explicitly known and agreed upon. This ensures that both parties are
        fully aware of their obligations, preventing disputes or
        misunderstandings. The compensation can be monetary or in the form of
        tangible benefits. For example, it is permissible to hire a domestic
        worker not only for a set salary but also in exchange for providing them
        with essential needs such as food, clothing, shelter, and healthcare.
        This flexibility in compensation reflects the ethical and inclusive
        nature of Ijaarah, ensuring that the arrangement is fair and beneficial
        to both parties while upholding the principles of justice in Islamic
        contracts.
        <br />
        <br /> iii) Permissibility of Use : The third essential condition of
        Ijaarah is that the item, asset, or benefit being leased or hired must
        comply with Islamic law. This ensures that the transaction aligns with
        Shariah principles and avoids activities that are impermissible or
        harmful. For example, a property can be rented for lawful purposes, such
        as living or conducting a business permissible in Islam, but it cannot
        be leased to operate a liquor shop or any activity deemed haram.
        Similarly, a tutor can be hired to teach subjects like Arabic grammar or
        other beneficial knowledge, but not for teaching black magic or any
        prohibited practice. This condition preserves the ethical foundation of
        Ijaarah and ensures that all parties uphold their moral and religious
        responsibilities within the contract.
        <br /> <br />
        <b>Hiring Married Women: The Importance of Spousal Permission</b>
        <br />
        <br />
        In Islamic law, it is not permissible to hire a married woman as an
        employee without the explicit permission of her husband, particularly if
        the employment could prevent her from fulfilling her marital
        obligations. This condition underscores the importance Islam places on
        maintaining harmony within the family unit and ensuring that the rights
        and responsibilities between spouses are respected. The husband's
        consent ensures that the employment will not lead to neglect of
        essential duties or disrupt the balance of the marital relationship.
        This principle seeks to promote mutual understanding, respect, and the
        prioritization of family responsibilities in line with Shariah
        teachings.
        <br />
        <br />
        <b>The Condition of Seeing or Describing the Leased Asset</b>
        <br />
        <br /> In Ijaarah, it is a condition that the object, asset, or benefit
        being leased or hired must be either seen or clearly described to avoid
        ambiguity. For real estate and agricultural land, many scholars
        emphasize that visual inspection is compulsory, as these properties
        often cannot be fully understood through description alone. For items
        rented for their specific attributes, such as sound, additional measures
        may be necessary. For example, if renting an alarm clock, its sound must
        be heard beforehand unless it is produced by a reputable manufacturer
        and comes with a warranty, in which case this requirement may be
        relaxed. Similarly, if hiring a rooster for its morning crow, the sound
        must be evaluated to ensure it meets the hirer's expectations. This
        condition reinforces transparency and fairness, ensuring all parties
        understand the qualities and value of the asset or service being
        contracted. <br /> <br />
        <b>Restricting Ijaarah to Benefits Without Ownership</b>
        <br />
        <br />
        The contract of renting or hiring (Ijaarah) must be strictly confined to
        deriving the agreed-upon benefit from the asset or service, without
        granting ownership, consumption, or disposal rights. This distinguishes
        Ijaarah from a sale, where ownership of the asset is transferred. For
        example, a person cannot rent a jar full of dates with the intention of
        eating the dates, as this would amount to consuming the asset itself,
        which is contrary to the nature of Ijaarah. Instead, the rented asset
        must remain intact or usable only for the specified purpose. This
        condition ensures clarity and preserves the essence of Ijaarah as a
        transaction based on temporary usage rights rather than transfer of
        ownership.
        <br />
        <br /> <b>Renting Waqf Properties: A Permissible Practice</b>
        <br />
        <br />
        Waqf, in Islamic law, refers to an endowment where an asset or property
        is dedicated for charitable or religious purposes, with its benefits or
        income serving the intended cause perpetually. Once designated as waqf,
        the asset itself cannot be sold, gifted, or inherited, ensuring its
        continuous use for the welfare of society. However, it is permissible to
        rent waqf properties, as long as the income generated is utilized for
        the specific objectives of the waqf. For instance, a building
        established as waqf for educational purposes can be rented out, and the
        proceeds can fund scholarships or maintain the institution. Renting waqf
        properties allows for their effective utilization while preserving the
        sanctity and purpose of the waqf, ensuring long-term benefits for the
        community.
        <br />
        <br />
        <b>
          Termination of Ijaarah Due to Non-Existence of the Asset or Service
        </b>
        <br />
        <br />
        The contract of hiring or leasing (Ijaarah) is automatically terminated
        if the rented item or service ceases to exist or becomes unusable. For
        example, if a house rented by a tenant collapses due to a natural
        disaster, the contract ends, and the tenant is only required to pay rent
        for the duration they lived in the house, not for the entire agreed
        period. Similarly, if a woman is hired to breastfeed a child and the
        child passes away, the contract is annulled. Likewise, if an animal is
        hired for transportation and it dies before completing the journey, the
        contract is terminated unless a substitute of the same kind can be
        provided. These provisions ensure fairness and protect both parties from
        unjust obligations when unforeseen circumstances render the contract's
        fulfillment impossible.
        <br />
        <br />
        <b>Responsibility of Rent Payment Despite Losses</b>
        <br />
        <br /> In Ijaarah, if the rented asset remains intact and available for
        use, the contract is not annulled even if the tenant suffers losses or
        is unable to derive the expected benefits. For instance, if a person
        rents a grocery store but fails to generate profits, they are still
        obligated to pay the agreed-upon rent. Similarly, if someone leases
        agricultural land and cannot find buyers for their crops, the rent
        remains payable. The principle ensures that the tenant fulfills their
        contractual obligations, as the lessor has provided access to the
        agreed-upon asset, regardless of the tenant's personal circumstances or
        outcomes. This emphasizes the mutual responsibility inherent in Ijaarah
        contracts.
        <br />
        <br />
        <b>Continuity of Ijaarah Contracts After Death</b>
        <br />
        <br />
        The contract of Ijaarah is not terminated by the death of either the
        lessor or the lessee, provided the rented asset or service remains
        intact and available. In such cases, the contract's obligations transfer
        to the heirs of the deceased party. For example, if a landlord passes
        away, the inheritors must honor the existing contract, allowing the
        tenant to continue residing in the property until the agreed-upon tenure
        expires. This principle ensures stability and fairness in Ijaarah
        agreements, protecting the rights of both parties even in unforeseen
        circumstances.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today’s lesson, we explored the concept of Ijaarah, the Islamic
        contract of hiring and leasing. We learned that Ijaarah is a permissible
        and widely practiced arrangement in Islamic law, provided it fulfills
        key conditions such as clarity in benefit, compensation, and
        permissibility of use. The lesson emphasized the importance of
        transparency, ethical use, and mutual consent in such contracts. We also
        discussed specific rulings, including the hiring of married women,
        renting waqf properties, termination due to loss or death, and the
        distinction between Ijaarah and ownership. Overall, Ijaarah reflects
        Islam’s commitment to fairness, justice, and mutual benefit in financial
        dealings.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        After exploring the principles of Ijaarah and the ethical framework
        surrounding hiring and leasing in Islam, we now move into a deeply
        virtuous and socially supportive practice i.e Ariyah (Lending for Use
        Without Gain). In Day 21, we will study the spiritual and legal
        significance of lending items without expecting compensation. This
        lesson will focus on the concepts of trust, responsibility, and
        generosity in interpersonal dealings, along with the rulings related to
        loss, misuse, and the obligations of borrowers and lenders. Prepare to
        deepen your understanding of how Islam promotes mutual assistance while
        safeguarding rights and encouraging goodwill.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <p className="content">
        1. What is a key requirement for a valid Ijaarah contract in Islamic
        law?
        <ul>
          <li>A. The compensation must be paid in gold only</li>
          <li>B. The benefit must be clearly defined and agreed upon</li>
          <li>C. The asset must be given as a gift afterward</li>
          <li>D. The lessee must be a Muslim</li>
        </ul>
        2. According to Islamic law, can waqf (endowment) properties be rented?
        <ul>
          <li>A. No, because they are for religious use only</li>
          <li>B. Yes, but only to family members</li>
          <li>C. Yes, if the rent supports the waqf’s intended purpose</li>
          <li>D. Yes, but only if the property is residential</li>
        </ul>
        3. What happens to an Ijaarah contract if the rented asset is destroyed
        during the lease period?
        <ul>
          <li>A. The contract continues until the term ends</li>
          <li>B. The lessee must pay the full rent</li>
          <li>C. The contract is terminated immediately</li>
          <li>D. The lessee becomes the owner of the asset</li>
        </ul>
        4. In the case of hiring a married woman for work, what is required
        under Islamic law?
        <ul>
          <li>A. The husband’s explicit permission</li>
          <li>B. A male guardian must co-sign</li>
          <li>C. She must be single</li>
          <li>D. She must only work from home</li>
        </ul>
      </p>

     
       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage20;
